import {
  Checkbox,
  DatePicker,
  FieldContainer,
  KeyboardKey,
  MultipleTextInputField,
  Select,
  TextArea,
  TextField,
} from '@oy/uidesktop';
import { Control, Controller } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import { useFormHelper, useTranslate } from 'src/hooks';
import { formatStringToDate } from 'src/utilities/helpers';
import * as yup from 'yup';
import {
  getAreaTextPlaceholder,
  getMultipleInputPlaceholder,
  getSelectPlaceholder,
} from '../helper';
import styles from './AutoReportCustomField.module.css';
import { LABEL } from './constants';
type AutoReportCustomFieldProps = {
  control: Control<
    {
      [x: string | number | symbol]: any;
    },
    any
  >;
  isRequired: boolean;
  isDisabled: boolean;
  name: string;
  options?: string[];
  type: string;
  parameter: string;
  isErrorMessageVisible: boolean;
};

export const AutoReportCustomField = ({
  control,
  name,
  isRequired,
  isDisabled,
  options,
  type,
  parameter,
  isErrorMessageVisible,
}: AutoReportCustomFieldProps) => {
  const { bindInput } = useFormHelper();
  const { getTranslation } = useTranslate();

  const getField = (props: any) => {
    switch (props.type) {
      case 'INPUT_TEXT':
        return <TextField {...props} disabled={isDisabled} />;
      case 'DROPDOWN':
        const transformedArray = props.options.map((item: string) => ({
          value: item,
          label: item,
        }));

        return (
          <Select
            value={
              props.value
                ? transformedArray.find(
                    (item: { value: string }) => item.value === props.value,
                  )
                : null
            }
            onChange={e => {
              props.onChange(e.value);
            }}
            options={transformedArray}
            isDisabled={isDisabled}
            errorMessage={isErrorMessageVisible && props.errorMessage}
            placeholder={getSelectPlaceholder(parameter)}
          />
        );
      case 'DATE_PICKER':
        return (
          <DatePicker
            selected={props.value && formatStringToDate(props.value)}
            onChange={e => {
              props.onChange(e);
            }}
            disabled={isDisabled}
            errorMessage={isErrorMessageVisible && props.errorMessage}
          />
        );
      case 'AREA_TEXT':
        return (
          <TextArea
            {...props}
            disabled={isDisabled}
            helpText={
              isErrorMessageVisible && props.errorMessage
                ? undefined
                : getTranslation('settings_autoReport_helper_bodyEmail')
            }
            placeholder={getAreaTextPlaceholder(parameter)}
          />
        );
      case 'MULTIPLE_INPUT':
        return (
          <MultipleTextInputField
            key={props.value ? props.value.toString() : null}
            disabled={isDisabled}
            onChange={e => {
              props.onChange(e.data);
            }}
            triggerKeys={[KeyboardKey.Tab, KeyboardKey.Enter]}
            data={Array.isArray(props.value) ? props.value : []}
            errorMessage={isErrorMessageVisible && props.errorMessage}
            helpText={
              isErrorMessageVisible && props.errorMessage
                ? undefined
                : getTranslation('settings_autoReport_helper_emailRecipients')
            }
            validator={(value: string) => {
              const schema = yup.string().email();
              return schema.isValidSync(value);
            }}
            placeholder={getMultipleInputPlaceholder(parameter)}
          />
        );
      case 'CHECK_BOX':
        return (
          <div className={styles.checkboxContainer}>
            {props.options.map((option: string) => {
              return (
                <Checkbox
                  key={option}
                  type="box"
                  disabled={isDisabled}
                  name={option}
                  title={option}
                  checked={props.value ? props.value.includes(option) : false}
                  onChange={e => {
                    const { checked, name } = e.target;
                    const currentValue = props.value || []; // Use default value for empty array

                    const newValue = checked
                      ? [...currentValue, name] // Add name if checked
                      : currentValue.filter((item: string) => item !== name); // Remove name if unchecked

                    props.onChange?.(newValue); // Handle optional onChange prop
                  }}
                />
              );
            })}
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldContainer
          subLabelPosition="horizontal"
          subLabel={isRequired ? undefined : '(optional)'}
          label={getTranslation(LABEL[name] ?? `field_${name.toLowerCase()}`)}
          isRequired={isRequired}
        >
          <div id="field-auto-report-id">
            {getField({
              type,
              ...bindInput(field, fieldState),
              options: options,
            })}
          </div>
          <Tooltip
            hidden={!isDisabled}
            anchorSelect="#field-auto-report-id"
            place="bottom"
          >
            {getTranslation('settings_autoReport_messageTooltip_disabledField')}
          </Tooltip>
        </FieldContainer>
      )}
    />
  );
};
